import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CrmSource } from '../api/types';
import { fetchCrmCapabilities } from '../api/salesforce';

/**
 * The keys of the capabilities body that are CRM SOURCES. Listed explicitly
 * rather than walked with `Object.entries` because the body also carries a
 * non-source sibling (`integrations`), and a generic walk would be at the
 * mercy of any future sibling that happens to expose a `connected` field.
 */
export const CRM_SOURCE_KEYS = ['salesforce', 'hubspot', 'zoho'] as const satisfies readonly CrmSource[];
// Adding a provider to `CrmSource` must break the build here, not silently
// leave it out of every derived list.
const _everySourceListed: Record<CrmSource, true> = { salesforce: true, hubspot: true, zoho: true };
void _everySourceListed;

/**
 * Did the capability probe answer?
 *
 * `ready` means we currently hold an answer for this company — either the
 * backend told us, per provider, what it's granted (including "nothing"),
 * or we held a prior answer and a LATER refetch failed (react-query keeps
 * `data` across a failed refetch, and a card that was live a moment ago has
 * no reason to be greyed out because a background refresh hiccuped). `unknown`
 * means we have never been able to ask. Both fail closed at the affordance
 * level while there is no data — but only `unknown` should stand between an
 * admin and the Integrations page: greying three cards because one probe
 * 500'd would claim nothing is granted when all we know is that we couldn't
 * check.
 */
export type CrmProbeStatus = 'loading' | 'ready' | 'unknown';

/**
 * Per-source CRM capability hook.
 *
 * `connectedSources` — providers with a stored credential that are also
 * enabled (the backend forces `connected` false for a provider that is off).
 * Drives Clean / Match / Send on the Batch screen.
 *
 * `enabledSources` / `isSourceEnabled` — the company's per-provider
 * ENTITLEMENT (`companies.crm_<source>_enabled`), which is a different
 * question from "has this company connected anything yet". Drives whether an
 * Integrations card is live or greyed. Fails closed: loading, an error, and a
 * backend deployed before the flag (no `enabled` bit) all read not enabled.
 *
 * Both lists are memoised over `q.data` so consumers' own `useMemo` /
 * `useCallback` keyed on them stay stable (e.g. `BatchRoute`'s `crmApi`).
 *
 * `probeStatus` fails closed only while there is NO data: with no data,
 * `q.isSuccess`/`q.isError` are both false between retry attempts, which
 * reads as `loading` — including the gap right after `retry()` resets a
 * failed probe back to pending. Once an answer has been held at least once,
 * it stays `ready` even through a later failed refetch, since `enabledSources`
 * / `connectedSources` still reflect that held answer rather than reverting
 * to empty.
 */
export function useCrmCapabilities(): {
  connectedSources: CrmSource[];
  enabledSources: CrmSource[];
  isSourceEnabled: (source: CrmSource) => boolean;
  probeStatus: CrmProbeStatus;
  isLoading: boolean;
  /** Re-runs the capability probe — the Retry behind an `unknown`. */
  retry: () => void;
} {
  const q = useQuery({
    queryKey: ['crmCapabilities'],
    queryFn: fetchCrmCapabilities,
    staleTime: 5 * 60_000,
  });
  const connectedSources = useMemo(
    () => CRM_SOURCE_KEYS.filter((key) => q.data?.[key]?.connected === true),
    [q.data],
  );
  const enabledSources = useMemo(
    () => CRM_SOURCE_KEYS.filter((key) => q.data?.[key]?.enabled === true),
    [q.data],
  );
  const isSourceEnabled = useCallback(
    (source: CrmSource) => enabledSources.includes(source),
    [enabledSources],
  );
  // isSuccess covers a success with no body (a 204 leaves data undefined);
  // data covers an answer we hold across a failed refetch.
  const probeStatus: CrmProbeStatus =
    q.isSuccess || q.data !== undefined ? 'ready' : q.isError ? 'unknown' : 'loading';

  const { refetch } = q;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    connectedSources,
    enabledSources,
    isSourceEnabled,
    probeStatus,
    isLoading: probeStatus === 'loading',
    retry,
  };
}
